import { THeaderType } from 'components/Header/Header'
import LinkWithLocale from 'components/LinkWithLocale'
import React, { FC } from 'react'
import arrowRightDropdownItem from 'assets/svg/arrowRightDropdownItem.svg'
import useTranslation from 'hooks/useTranslation'
import { TFunction } from 'src/providers/TranslateProvider/TranslateProvider'
import { BadgeType } from 'src/types'
import styled from 'styled-components'
import BadgeDropdown from '../BadgeDropdown'
import SetupButton from './components/SetupButton'
import { GATSBY_MAIN_URL, isEmptyHeaderPage } from '@constants'

import useLocale from 'hooks/useLocale'

const getOptions = (t: TFunction) => [
  {
    name: t('Bitcoin Price Alerts'),
    text: t('Get notified when Bitcoin goes above or below a target price'),
    url: '/crypto-alerts/track-bitcoin-btc-price',
    badgeType: BadgeType.Hot,
  },
  {
    name: t('Top 10 Trading Volume'),
    text: t('Setup crypto alerts for top 10 trading volume coins'),
    url: '/crypto-alerts/top-10-trade-volume',
    badgeType: null,
  },
  {
    name: t('Сrypto Volume Alerts'),
    text: t('Get notified of unusual changes in trading volumes on exchanges'),
    url: '/crypto-alerts/crypto-volume-alerts',
    badgeType: null,
  },
  {
    name: t('ETH Gas Tracker'),
    text: t('Get notified when ETH Gas price drops'),
    url: '/crypto-alerts/eth-gas-tracker',
    badgeType: BadgeType.New,
  },
]

interface DropdownPriceAlertsProps extends IOwnProps {
  dropdownType:
    | 'app'
    | 'buy crypto'
    | 'futures'
    | 'spot'
    | 'explore trading'
    | 'price alerts'
    | 'staking'
    | 'markets'
    | 'refferal'
}

const DropdownPriceAlerts: FC<DropdownPriceAlertsProps> = () => {
  const { t } = useTranslation()

  const { pathname } = useLocale()

  const isBlog = isEmptyHeaderPage(pathname)

  return (
    <DropdownItemGridContainer>
      <ItemWrapper>
        {getOptions(t).map((item, index) => {
          const to = isBlog ? `/page${item.url}` : item.url

          return (
            <DropdownItem key={index} to={to} withLocale={false} replace>
              <NameWrapper>
                <DropdownItemName>{item.name}</DropdownItemName>
                <BadgeDropdown type={item.badgeType} />
              </NameWrapper>
              <DropdownItemText>{item.text}</DropdownItemText>
            </DropdownItem>
          )
        })}
        <SetupButton
          withLocale={false}
          href={`${GATSBY_MAIN_URL}/page/crypto-alerts`}
          label={t('Setup Price Alerts')}
        />
      </ItemWrapper>
    </DropdownItemGridContainer>
  )
}

export default DropdownPriceAlerts

const DropdownItemGridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  column-gap: 2.75rem;
  background: linear-gradient(0deg, #f3f7fd, #f3f7fd), #123482;
  border-radius: 0.5rem;
`

const ItemWrapper = styled.div`
  display: grid;
  grid-template-rows: max-content;
  grid-auto-rows: max-content;
  grid-column-gap: 3rem;
  grid-row-gap: 0.25rem;
  padding: 2rem;
`

const DropdownItem = styled(LinkWithLocale)`
  display: block;
  position: relative;
  padding: 0.25rem 0 0.75rem 0;
  padding-inline-end: 2.75rem;
  padding-inline-start: 0.75rem;
  clear: both;
  font-weight: ${({ theme: { fontWeight } }) => fontWeight.normal};
  color: ${({ theme: { colors } }) => colors.dropdownItem};
  text-align: inherit;
  white-space: nowrap;
  background-color: transparent;
  border: 0;
  line-height: 1.375;
  transition: color 0.2s, background-color 0.2s;
  text-align: start;

  &:hover {
    > span {
      color: ${({ theme: { colors } }) => colors.link};
    }

    text-decoration: none;
    background-color: ${({ theme: { colors } }) => colors.white};

    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      height: 100%;
      width: 2rem;
      border-radius: 0.25rem;
      background-image: url(${arrowRightDropdownItem});
      background-repeat: no-repeat;
      background-position: center;
      background-color: ${({ theme: { colors } }) => colors.newLightColor.vividBlue};

      [dir='rtl'] & {
        left: 0;
        right: unset;
        transform: rotate(180deg);
      }
    }
  }
`
const DropdownItemName = styled.span`
  color: ${({ theme: { colors } }) => colors.newLightColor.softNavy};
  font-weight: ${({ theme: { fontWeight } }) => fontWeight.semibold};
  font-size: 0.875rem;
  margin-inline-end: 0.5rem;
`
const DropdownItemText = styled.p`
  width: 240px;
  margin: 0;
  margin-top: 2px;
  margin-inline-end: auto;
  white-space: pre-wrap;
  color: ${({ theme: { colors } }) => colors.newLightColor.softNavy};
  font-weight: ${({ theme: { fontWeight } }) => fontWeight.normal};
  font-size: 0.875rem;
  opacity: 0.8;
`
const NameWrapper = styled.div`
  align-items: center;
  min-width: 246px;
  text-align: start;
`

interface IOwnProps {
  name?: string
  options: Array<{ name: string; url: string }>
  warning?: string | null
  type: THeaderType
}
